import type { DialogueAction, Manifest, Product } from "../manifest/schema.js";
import type { DialogueProvider, DialogueReply, DialogueSession } from "../dialogue/provider.js";
import type { Basket } from "../shop/basket.js";
import { PRICE_ON_REQUEST, formatGbp, totalsText } from "../shop/basket.js";
import { clear, h } from "./dom.js";
import type { ProductLists } from "../featurepad/lists.js";

export interface PanelDeps {
  manifest: Manifest;
  basket: Basket;
  mediaUrl: (rel: string) => string;
  travelToShop: (shopId: string) => void;
  announce: (msg: string) => void;
  /** Wish list (heart, priced items) and saved items. Optional. */
  lists?: ProductLists;
}

/** Modal panel host: one at a time, Escape closes, focus returns to the opener. */
export class Modal {
  private opener: Element | null = null;
  readonly root = h("div", { class: "modal", role: "dialog", "aria-modal": "true", hidden: true });

  constructor(private readonly label: string) {
    this.root.setAttribute("aria-label", label);
    this.root.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        this.close();
      } else if (e.key === "Tab") {
        // Focus trap: keep Tab / Shift+Tab inside the open panel.
        const f = [...this.root.querySelectorAll<HTMLElement>("button, [href], select, input, textarea, [tabindex]:not([tabindex='-1'])")].filter((el) => !el.hasAttribute("disabled"));
        if (f.length === 0) return;
        const first = f[0]!, last = f[f.length - 1]!;
        if (e.shiftKey && (document.activeElement === first || !this.root.contains(document.activeElement))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
    this.root.addEventListener("click", (e) => {
      if (e.target === this.root) this.close();
    });
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(content: Node, label?: string): void {
    if (!this.root.hidden) {
      const done = this.onClose;
      this.onClose = undefined;
      done?.();
    }
    this.opener = document.activeElement;
    this.root.setAttribute("aria-label", label ?? this.label);
    clear(this.root);
    const close = h("button", { class: "modal-close", "aria-label": `Close ${this.label}`, onclick: () => this.close() }, "×");
    this.root.append(h("div", { class: "modal-card" }, close, content));
    this.root.hidden = false;
    ((this.root.querySelector("[data-autofocus]") ?? close) as HTMLElement).focus();
  }

  /** Called once the next time the panel closes (e.g. to stop a story that is playing). */
  onClose: (() => void) | undefined;

  close(): void {
    if (this.root.hidden) return;
    const done = this.onClose;
    this.onClose = undefined;
    done?.();
    this.root.hidden = true;
    clear(this.root);
    if (this.opener instanceof HTMLElement) this.opener.focus();
  }
}

export function directoryContent(d: PanelDeps, close: () => void): HTMLElement {
  const byDistrict = d.manifest.districts.map((dist) => {
    const shops = d.manifest.shops.filter((s) => s.districtId === dist.id);
    return h(
      "section",
      { class: "dir-district" },
      h("h3", {}, `${dist.name} `, h("small", {}, dist.level)),
      h(
        "ul",
        {},
        ...shops.map((s) =>
          h(
            "li",
            { class: "dir-shop", style: `--accent:${s.accent}` },
            h("div", {}, h("strong", {}, s.name), h("span", { class: "muted" }, ` ${s.category}`), h("p", {}, s.tagline)),
            h("button", { class: "btn", "data-shop": s.id, onclick: () => (close(), d.travelToShop(s.id)) }, `Take me to ${s.name}`)
          )
        )
      )
    );
  });
  return h("div", {}, h("h2", { "data-autofocus": true, tabindex: -1 }, "Mall directory"), ...byDistrict);
}

export function productContent(p: Product, d: PanelDeps): HTMLElement {
  let colour = p.colours[0]?.name ?? "";
  const sizeSelect = h("select", { id: "size", "aria-label": "Size" }, ...p.sizes.map((s) => h("option", { value: s }, s)));
  const status = h("p", { class: "status", role: "status" });
  const swatches = h(
    "div",
    { class: "swatches", role: "radiogroup", "aria-label": "Colour" },
    ...p.colours.map((c, i) =>
      h("button", {
        class: i === 0 ? "swatch is-selected" : "swatch",
        role: "radio",
        "aria-checked": i === 0,
        "aria-label": c.name,
        title: c.name,
        style: `background:${c.hex}`,
        onclick: (e) => {
          colour = c.name;
          swatches.querySelectorAll(".swatch").forEach((s) => {
            s.classList.remove("is-selected");
            s.setAttribute("aria-checked", "false");
          });
          (e.currentTarget as HTMLElement).classList.add("is-selected");
          (e.currentTarget as HTMLElement).setAttribute("aria-checked", "true");
          colourName.textContent = c.name;
        }
      })
    )
  );
  const colourName = h("span", { class: "muted" }, colour);
  const img = h("img", { class: "product-img", src: `${d.mediaUrl(p.image)}.jpg`, alt: p.name });
  return h(
    "div",
    { class: "product" },
    img,
    h(
      "div",
      { class: "product-info" },
      h("p", { class: "muted" }, p.brand),
      h("h2", { "data-autofocus": true, tabindex: -1 }, p.name),
      h("p", { class: "price" }, p.pricePence === undefined ? "Price on request" : formatGbp(p.pricePence)),
      h("p", {}, p.description),
      p.material ? h("p", { class: "muted" }, p.material) : null,
      h("div", { class: "field" }, h("span", {}, "Colour: "), colourName, swatches),
      h("label", { class: "field" }, h("span", {}, "Size "), sizeSelect),
      p.pricePence === undefined
        ? h(
            "button",
            {
              class: "btn btn-primary",
              "data-testid": "request-viewing",
              onclick: () => {
                status.textContent = "Thank you - a specialist will be in touch to arrange a private viewing (demo).";
                d.announce(`Viewing requested for ${p.name}`);
              }
            },
            "Request a private viewing"
          )
        : h(
        "button",
        {
          class: "btn btn-primary",
          "data-testid": "add-to-basket",
          onclick: () => {
            d.basket.add(p.id, colour, (sizeSelect as HTMLSelectElement).value);
            status.textContent = `Added to basket (${d.basket.count} item${d.basket.count === 1 ? "" : "s"}).`;
            d.announce(`${p.name} added to basket`);
          }
        },
        "Add to basket"
      ),
      listButtons(p, d, status),
      status,
      h("p", { class: "muted small" }, `SKU ${p.sku}. Demo product, not for sale.`)
    )
  );
}

export function basketContent(d: PanelDeps, onDone: () => void): HTMLElement {
  const root = h("div", { class: "basket" });
  // Last code message, kept across re-renders so the shopper sees why a code was accepted or rejected.
  let promoMsg: { ok: boolean; text: string } | null = null;
  const promoForm = (): HTMLElement => {
    const { basket } = d;
    const applied = basket.promo;
    const msg = promoMsg ? h("p", { class: `promo-msg small${promoMsg.ok ? "" : " promo-msg--error"}`, role: promoMsg.ok ? "status" : "alert", "data-testid": "promo-message", id: "promo-msg" }, promoMsg.text) : null;
    if (applied) {
      return h(
        "div",
        { class: "promo" },
        h("span", { class: "promo-applied" }, `Code ${applied.code} applied`),
        h("button", { class: "btn btn-small", "data-testid": "promo-remove", "aria-label": `Remove code ${applied.code}`, onclick: () => ((promoMsg = { ok: true, text: `Code ${applied.code} removed.` }), basket.removeCode(), render()) }, "Remove"),
        msg
      );
    }
    const input = h("input", { id: "promo-code", type: "text", name: "promo", autocomplete: "off", autocapitalize: "characters", spellcheck: "false", maxlength: 24, "data-testid": "promo-input", "aria-describedby": promoMsg ? "promo-msg" : undefined, "aria-invalid": promoMsg && !promoMsg.ok ? "true" : undefined });
    return h(
      "form",
      {
        class: "promo",
        "aria-label": "Discount code",
        onsubmit: (e: Event) => {
          e.preventDefault();
          const r = basket.applyCode(input.value);
          promoMsg = { ok: r.ok, text: r.message };
          d.announce(r.message);
          render();
          (root.querySelector(r.ok ? "[data-testid='promo-remove']" : "[data-testid='promo-input']") as HTMLElement | null)?.focus();
        }
      },
      h("label", { for: "promo-code" }, "Discount code"),
      input,
      h("button", { class: "btn btn-small", type: "submit", "data-testid": "promo-apply" }, "Apply"),
      msg
    );
  };
  const render = () => {
    clear(root);
    const { basket, manifest } = d;
    root.append(h("h2", { "data-autofocus": true, tabindex: -1 }, "Your basket"));
    if (basket.lines.length === 0) {
      root.append(h("p", {}, "Your basket is empty. Pick something up from a shop floor."));
      return;
    }
    const items = h("ul", { class: "basket-lines" });
    basket.lines.forEach((l, i) => {
      const p = manifest.products.find((x) => x.id === l.productId);
      if (!p) return;
      const thumb = l.image
        ? h("img", { class: "line-img line-img--wide", src: d.mediaUrl(l.image), alt: "", width: 96, height: 54 })
        : h("img", { src: `${d.mediaUrl(p.image)}.jpg`, alt: "", width: 56, height: 70 });
      items.append(
        h(
          "li",
          {},
          thumb,
          h(
            "div",
            { class: "grow" },
            h("strong", {}, p.name),
            h("div", { class: "muted small" }, `${p.brand} · ${l.colour} · ${l.size}`),
            l.detail ? h("div", { class: "muted small", "data-testid": "line-detail" }, l.detail) : null
          ),
          h(
            "div",
            { class: "qty" },
            h("button", { "aria-label": `Decrease quantity of ${p.name}`, onclick: () => (basket.setQty(i, l.qty - 1), render()) }, "−"),
            h("span", { "aria-live": "polite" }, String(l.qty)),
            h("button", { "aria-label": `Increase quantity of ${p.name}`, onclick: () => (basket.setQty(i, l.qty + 1), render()) }, "+")
          ),
          h("div", { class: "line-price" }, p.pricePence === undefined ? PRICE_ON_REQUEST : formatGbp(p.pricePence * l.qty))
        )
      );
    });
    const t = totalsText(basket);
    root.append(
      items,
      promoForm(),
      h(
        "dl",
        { class: "totals" },
        h("dt", {}, "Subtotal"),
        h("dd", {}, t.subtotal),
        t.discount ? h("dt", { "data-testid": "discount-label" }, `Discount: ${t.discountLabel ?? ""}`) : null,
        t.discount ? h("dd", { "data-testid": "basket-discount" }, t.discount) : null,
        h("dt", {}, "Delivery"),
        h("dd", {}, t.delivery),
        h("dt", {}, "Total"),
        h("dd", { "data-testid": "basket-total" }, t.total)
      ),
      h(
        "button",
        {
          class: "btn btn-primary",
          "data-testid": "place-order",
          onclick: () => {
            const ref = Date.now().toString(36).toUpperCase().slice(-6);
            const tt = totalsText(basket);
            const n = basket.count;
            basket.clear();
            clear(root);
            root.append(
              h("h2", { "data-autofocus": true, tabindex: -1 }, "Thank you"),
              h(
                "p",
                { "data-testid": "order-confirmation" },
                tt.enquiryOnly
                  ? `Demo enquiry ENQ-${ref} recorded for ${n} item${n === 1 ? "" : "s"}, ${PRICE_ON_REQUEST.toLowerCase()}. No payment was taken and nothing was sent.`
                  : `Demo order RED-${ref} for ${tt.total} placed. No payment was taken and nothing will be sent.`
              ),
              h("button", { class: "btn", onclick: onDone }, "Continue shopping")
            );
            (root.querySelector("[data-autofocus]") as HTMLElement).focus();
          }
        },
        t.enquiryOnly ? "Send demo enquiry" : "Place demo order"
      ),
      h("p", { class: "muted small" }, t.enquiryOnly ? "Demo enquiry: nothing is sent and no payment details are collected." : "Demo checkout: no payment details are collected.")
    );
  };
  render();
  return root;
}

/** Conversation overlay driven by any DialogueProvider. */
export class DialoguePanel {
  readonly root = h("section", { class: "dialogue", "aria-label": "Conversation", hidden: true });
  private session: DialogueSession | undefined;
  private characterId = "";

  constructor(
    private readonly provider: DialogueProvider,
    private readonly manifest: Manifest,
    private readonly basket: Basket,
    private readonly getLocation: () => string,
    private readonly onAction: (a: DialogueAction) => void
  ) {}

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  async open(characterId: string): Promise<void> {
    const character = this.manifest.characters.find((c) => c.id === characterId);
    if (!character) return;
    this.characterId = characterId;
    this.show(character.name, await this.provider.open(this.context()));
  }

  close(): void {
    this.root.hidden = true;
    clear(this.root);
    this.session = undefined;
  }

  private context() {
    const character = this.manifest.characters.find((c) => c.id === this.characterId);
    if (!character) throw new Error(`unknown character ${this.characterId}`);
    return { character, locationId: this.getLocation(), basketCount: this.basket.count };
  }

  private show(name: string, reply: DialogueReply): void {
    this.session = reply.session;
    for (const a of reply.actions) this.onAction(a);
    if (reply.ended && !reply.text) return this.close();
    const role = this.manifest.characters.find((c) => c.id === this.characterId)?.role ?? "";
    clear(this.root);
    const opts = h("div", { class: "choices" });
    reply.choices.forEach((c, i) => {
      const b = h("button", { class: "btn choice", "data-choice": c.id, onclick: () => void this.choose(name, c.id) }, c.text);
      if (i === 0) b.setAttribute("data-autofocus", "");
      opts.append(b);
    });
    this.root.append(
      h("div", { class: "dialogue-head" }, h("strong", {}, name), h("span", { class: "muted" }, ` ${role}`), h("button", { class: "btn small", "aria-label": "End conversation", onclick: () => this.close() }, "Leave")),
      h("p", { class: "dialogue-say", role: "status" }, reply.text),
      opts
    );
    if (reply.ended) opts.append(h("button", { class: "btn choice", onclick: () => this.close() }, "Goodbye"));
    this.root.hidden = false;
    (this.root.querySelector("[data-autofocus]") as HTMLElement | null)?.focus();
  }

  private async choose(name: string, choiceId: string): Promise<void> {
    if (!this.session) return;
    this.show(name, await this.provider.respond(this.session, { type: "choice", choiceId }, this.context()));
  }
}

/** Large, touch-friendly destination buttons for a `menu` hotspot (e.g. the lift floor panel). */
export function menuContent(
  title: string,
  items: { badge?: string; label: string; to: string; current?: boolean }[],
  choose: (to: string) => void
): HTMLElement {
  return h(
    "div",
    { class: "menu-panel" },
    h("h2", { id: "menu-title", tabindex: -1 }, title),
    h(
      "ul",
      { class: "menu-list", "aria-labelledby": "menu-title" },
      ...items.map((i, n) =>
        h(
          "li",
          {},
          h(
            "button",
            {
              class: "menu-item",
              "data-menu-to": i.to,
              "data-autofocus": n === 0 ? true : undefined,
              "aria-current": i.current ? "location" : undefined,
              disabled: i.current ? true : undefined,
              onclick: () => choose(i.to)
            },
            i.badge ? h("span", { class: "menu-badge", "aria-hidden": "true" }, i.badge) : null,
            h("span", { class: "menu-label" }, i.badge ? h("span", { class: "sr-only" }, `${i.badge}, `) : null, i.label)
          )
        )
      )
    )
  );
}

/** Heart (wish list, priced items only) and Save (any item) toggles for the product panel. */
function listButtons(p: Product, d: PanelDeps, status: HTMLElement): HTMLElement | null {
  const lists = d.lists;
  if (!lists) return null;
  const mk = (kind: "wishlist" | "saved", on: string, off: string, testid: string, extraClass: string): HTMLElement => {
    const b = h("button", { type: "button", class: `btn btn-small ${extraClass}`, "data-testid": testid });
    const paint = () => {
      const has = lists.has(kind, p.id);
      b.setAttribute("aria-pressed", String(has));
      b.textContent = has ? on : off;
    };
    b.addEventListener("click", () => {
      const now = lists.toggle(kind, p.id);
      const where = kind === "wishlist" ? "wish list" : "saved items";
      status.textContent = now ? `Added to your ${where}.` : `Removed from your ${where}.`;
      d.announce(`${p.name} ${now ? "added to" : "removed from"} your ${where}`);
      paint();
    });
    paint();
    return b;
  };
  return h(
    "div",
    { class: "product-lists" },
    p.pricePence !== undefined ? mk("wishlist", "\u2665 On your wish list", "\u2661 Add to wish list", "wishlist-toggle", "heart") : null,
    mk("saved", "Saved", "Save for later", "save-toggle", "")
  );
}
